import notificationClient from '../../api/notificationClient';
import {
  Governance_Templates_Url,
  Governance_Template_Url,
  Governance_Variants_Url,
  Governance_Approval_Url,
  Governance_Permission_Url,
  Governance_Consent_Url,
  Governance_Suppression_Url,
  Governance_SendDecisions_Url,
  Admin_DndScrub_Url,
} from '../../api/endpoints';
import type {
  BackendTemplate,
  BackendTemplateDetail,
  BackendTemplateVariant,
  BackendTemplateApproval,
  BackendTemplatePermission,
  BackendSuppressionEntry,
  BackendSendDecision,
  MessageClassCode,
  ApprovalState,
  SuppressionKind,
  RecipientIdentity,
} from '../../schema/governance';
import type { ChannelType } from '../../schema/notification';

// ── Templates ────────────────────────────────────────────────────────────────

export const listTemplates = async (): Promise<BackendTemplate[]> => {
  const { data } = await notificationClient.get<{ templates: BackendTemplate[] }>(Governance_Templates_Url);
  return data.templates;
};

export const createTemplate = async (input: {
  templateKey: string;
  displayName: string;
  messageClassCode: MessageClassCode;
}): Promise<BackendTemplate> => {
  const { data } = await notificationClient.post<{ template: BackendTemplate }>(Governance_Templates_Url, input);
  return data.template;
};

export const getTemplateDetail = async (templateId: string): Promise<BackendTemplateDetail> => {
  const { data } = await notificationClient.get<BackendTemplateDetail>(Governance_Template_Url(templateId));
  return data;
};

export interface UpsertVariantInput {
  localeTag: string;
  channelKind: ChannelType;
  subject?: string;
  body: string;
  placeholderKeys: string[];
}

export interface UpsertVariantResult {
  templateVariantId: string;
  approvalsReset: Array<{ channelKind: ChannelType; regionCode: string }>;
  placeholderWarnings: string[];
}

export const upsertVariant = async (
  templateId: string,
  input: UpsertVariantInput,
): Promise<UpsertVariantResult> => {
  const { data } = await notificationClient.put<UpsertVariantResult>(Governance_Variants_Url(templateId), input);
  return data;
};

export const listVariants = async (templateId: string): Promise<BackendTemplateVariant[]> => {
  const { data } = await notificationClient.get<{ variants: BackendTemplateVariant[] }>(Governance_Variants_Url(templateId));
  return data.variants;
};

// ── Gate 1 — approval (admin) ─────────────────────────────────────────────────

export const listApprovals = async (templateId: string): Promise<BackendTemplateApproval[]> => {
  const { data } = await notificationClient.get<{ approvals: BackendTemplateApproval[] }>(Governance_Approval_Url(templateId));
  return data.approvals;
};

export const setApproval = async (
  templateId: string,
  input: {
    channelKind: ChannelType;
    regionCode: string;
    approvalState: ApprovalState;
    externalProvider?: string;
    externalTemplateId?: string;
  },
): Promise<void> => {
  await notificationClient.put(Governance_Approval_Url(templateId), input);
};

// ── Gate 2 — permission cube (admin) ─────────────────────────────────────────

export const listPermissions = async (templateId: string): Promise<BackendTemplatePermission[]> => {
  const { data } = await notificationClient.get<{ permissions: BackendTemplatePermission[] }>(Governance_Permission_Url(templateId));
  return data.permissions;
};

export const setPermission = async (
  templateId: string,
  input: {
    jurisdictionCode: string;
    channelKind: ChannelType;
    messageClassCode: MessageClassCode;
    isPermitted: boolean;
    reasonCode?: string;
  },
): Promise<void> => {
  await notificationClient.put(Governance_Permission_Url(templateId), input);
};

// ── Consent & suppression ────────────────────────────────────────────────────

export const recordConsent = async (input: {
  recipient: RecipientIdentity;
  channelKind: ChannelType;
  messageClassCode: MessageClassCode;
  consentState: 'granted' | 'withdrawn';
  basis?: string;
  evidenceRef?: string;
}): Promise<{ recipientHash: string }> => {
  const { data } = await notificationClient.post<{ recipientHash: string }>(Governance_Consent_Url, input);
  return data;
};

export const recordSuppression = async (input: {
  recipient: RecipientIdentity;
  channelKind: ChannelType;
  messageClassCode?: MessageClassCode;
  suppressionKind: SuppressionKind;
}): Promise<{ recipientHash: string }> => {
  const { data } = await notificationClient.post<{ recipientHash: string }>(Governance_Suppression_Url, input);
  return data;
};

export const listSuppressions = async (): Promise<BackendSuppressionEntry[]> => {
  const { data } = await notificationClient.get<{ suppressions: BackendSuppressionEntry[] }>(Governance_Suppression_Url);
  return data.suppressions;
};

// ── DND scrub ingestion (admin) ───────────────────────────────────────────────

export const upsertDndScrub = async (input: {
  recipient: RecipientIdentity;
  jurisdictionCode: string;
  registryName: string;
  isRegistered: boolean;
  validUntil?: string;
}): Promise<{ recipientHash: string }> => {
  const { data } = await notificationClient.post<{ recipientHash: string }>(Admin_DndScrub_Url, input);
  return data;
};

// ── "Why was this refused" drill-down ────────────────────────────────────────

export const getSendDecisions = async (recipient: RecipientIdentity): Promise<BackendSendDecision[]> => {
  const { data } = await notificationClient.get<{ decisions: BackendSendDecision[] }>(Governance_SendDecisions_Url, {
    params: recipient,
  });
  return data.decisions;
};
